import React, { useEffect, useMemo, useState } from "react"
import { makeStyles } from "@mui/styles"
import { TextField } from "@mui/material"
import { useCallback } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
  showSingleOrder,
  updateArticleChangeDeliveryNoteQuantities,
  showOrderDeliveryNote
} from "../../../actions/OrderReception/OrderReception"

import OrderReceptionHeader from "../Reception/OrderReceptionHeader"
import SupplierItemLotFooter from "../Lot/SupplierItemLotFooter"
import { getOrderSupplierItemReceptionSelector } from "../../../reducers/OrderReception/orderReception"
import { COLORS } from "../../../utils"
import { Table, TableBody, TableCell, TableContainer, TableRow, Paper, TablePagination } from "@mui/material"
import { generateStockUnitLabel } from "../../../utils/orderReception"

const useStyles = makeStyles({
  content: {
    padding: "24px 24px 100px 24px",
    display: "flex",
    flexDirection: "column",
    gap: 24,
    marginTop: 64,
    alignSelf: "stretch",
  },
  tableContent: {
    padding: 16,
    borderRadius: 8,
    background: COLORS.BLUE_100,
    "& td": {
      paddingLeft: 0,
      paddingRight: 0,
    }
  },
  tdInput: {
    textAlign: "right",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8
  },
  tdInputField: {
    width: 81,
    borderRadius: 8,
    "& .MuiInputBase-input": {
      textAlign: "center"
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 8
    }
  }
})

const OrderDeliveryNoteQuantities = () => {

  const dispatch = useDispatch()
  const classes = useStyles()

  const date = useSelector(state => state.orderReception.date)
  const site = useSelector(state => state.orderReception.site)

  const orderReception = useSelector(getOrderSupplierItemReceptionSelector)
  const [deliveryNotes, setDeliveryNotes] = useState({})

  useEffect(() => {
    if (orderReception) {
      setDeliveryNotes((orderReception.supplierItems || []).reduce((acc, item) => {
        // Pre-fill with reception quantity if deliveryNote is not set
        acc[item.supplierItem?.objectId] = item.quantities?.deliveryNote || item.quantities?.reception || ""
        return acc
      }, {}))
    }
  }, [orderReception])

  const isValid = useMemo(() => {
    return Object.values(deliveryNotes).filter(value => value).length > 0 && Object.values(deliveryNotes).every(value => value !== "" && !isNaN(Number(value)) && Number(value) >= 0)
  }, [deliveryNotes])

  const rows = useMemo(() => {
    if (!orderReception) return []
    return orderReception.supplierItems.map((item) => ({
      id: item.supplierItem?.objectId,
      name: item.supplierItem?.name,
      reference: item.supplierItem?.reference,
      quantity: (item.quantities?.reception) || "0",
      unit: generateStockUnitLabel(item.supplierItem)
    }))
  }, [orderReception])

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  // only for initial value
  useEffect(() => {
    if (!orderReception) return
  }, [])

  const onClickReturn = useCallback(() => {
    dispatch(showSingleOrder(site.objectId, date, orderReception.objectId))
  }, [site, date, orderReception])

  if (!orderReception) return

  const handleNumberChange = (e, id) => {
    let value = e.target.value
    // Replace comma with dot for decimal input
    value = value.replace(/,/g, ".")
    // Only allow numbers and one decimal point
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setDeliveryNotes(prev => ({ ...prev, [id]: value }))
    }
  }

  const handleSubmit = async () => {
    await dispatch(updateArticleChangeDeliveryNoteQuantities(orderReception.objectId, deliveryNotes))
    dispatch(showOrderDeliveryNote(site.objectId, date, orderReception.objectId))
  }

  return (
    <div className="flexColumn positionRelative flex1">
      <OrderReceptionHeader onBack={onClickReturn} infoText="/ ... / Contrôler le bon de livraison" />
      <div className={classes.content}>
        <div>
          <div className="flexRow center" style={{ gap: 8, marginBottom: 8 }}>
            <img alt="uil_chart" src="/icons/uil_chart.svg" />
            <b>Vérification des quantités</b>
          </div>
          <div>Contrôlez les quantités renseignées sur le bon de livraison.</div>
        </div>
        <div className={classes.tableContent}>
          <div className="flexRow spaceBetween">
            <b style={{ fontSize: "18px" }}>Commande n°{orderReception && orderReception.orderNumber}</b>
            <div>Quantité réceptionnée vs BL</div>
          </div>
          <TableContainer component={Paper} sx={{ mt: 2, pl: 2, pr: 2 }}>
            <Table>
              <TableBody>
                {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <div>
                        {row.name}
                        {row.reference && <div>réf:{row.reference}</div>}
                      </div>
                    </TableCell>
                    <TableCell className={classes.tdInput}>
                      <div>{row.quantity} ({row.unit})</div>
                      <div>/</div>
                      <TextField
                        className={classes.tdInputField}
                        size="small"
                        name={`quantity-${row.id}`}
                        value={deliveryNotes[row.id] || ""}
                        onChange={(e) => handleNumberChange(e, row.id)}
                        inputProps={{
                          inputMode: "decimal",
                          pattern: "[0-9]*[.,]?[0-9]*"
                        }}
                        helperText={deliveryNotes[row.id] && isNaN(Number(deliveryNotes[row.id])) ? "Nombre invalide" : ""}
                        error={deliveryNotes[row.id] && isNaN(Number(deliveryNotes[row.id]))}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
        <TablePagination
          sx={{ marginTop: -2 }}
          labelRowsPerPage="Lignes par page"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count} contenus`}
          component="div"
          count={rows.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </div>
      <SupplierItemLotFooter
        confirmText="VALIDER LES QUANTITÉS"
        onConfirm={handleSubmit}
        cancelText="Retour"
        confirmDisabled={!isValid}
        sx={{ position: "fixed !important", justifyContent: "space-between !important" }}
      />
    </div>
  )
}

export default OrderDeliveryNoteQuantities